import {
  relation,
  children,
  writer,
  field,
  date,
  lazy,
  json,
  text,
} from "@nozbe/watermelondb/decorators";
import {
  NEW_SORT,
  sanitizeFavourites,
  sanitizeSmsTemplate,
  SORT,
} from "../../constants";
import { uuid } from "../../oscar-pos-core/constants";
import { Model, Q } from "@nozbe/watermelondb";
import {
  CASHBOOK_CATEGORIES,
  POS_ORDERS_SCHEMA,
  SALE_ORDER_SCHEMA,
  BUSINESS_SCHEMA,
  CATEGORY_SCHEMA,
  CUSTOMER_SCHEMA,
  POS_TXNS_SCHEMA,
  INVOICE_SCHEMA,
  STOCK_SCHEMA,
  STAFF_SCHEMA,
  CART_SCHEMA,
  UNIT_SCHEMA,
  COMPANY,
  CASH_TXN_SCHEMA,
} from "../schema";
import {
  resolveCategoryDisplayName,
  findCategoryIdsByName,
  getCategoryNamesByIds,
} from "../../utils";

// Category filter values from the UI are display names (built off the same
// cached config as `getCategoriesByType`), but the stored `category` column
// on a record may hold either a legacy name (in any language) or the newer
// category id. Expand the selected names with any matching id(s) and their
// names in every language so the query matches all of them.
const expandCategoryFilterValues = (categories, groups) => {
  if (!categories?.length) return categories;
  const extraValues = groups.flatMap((group) =>
    categories.flatMap((name) => {
      const ids = findCategoryIdsByName(group, name);
      return [...ids, ...getCategoryNamesByIds(group, ids)];
    }),
  );
  return [...new Set([...categories, ...extraValues])];
};

export default class Business extends Model {
  static table = BUSINESS_SCHEMA;

  static associations = {
    user: { type: "belongs_to", key: "user_id" },
    group_payment_ledger: { type: "has_many", foreignKey: "business_id" },
    cashbook_categories: { type: "has_many", foreignKey: "business_id" },
    recharge_disputes: { type: "has_many", foreignKey: "business_id" },
    stock_categories: { type: "has_many", foreignKey: "business_id" },
    survey_responses: { type: "has_many", foreignKey: "business_id" },
    invoice_payments: { type: "has_many", foreignKey: "business_id" },
    pos_order_lines: { type: "has_many", foreignKey: "business_id" },
    rate_list_lines: { type: "has_many", foreignKey: "business_id" },
    inapp_messages: { type: "has_many", foreignKey: "business_id" },
    business_card: { type: "has_many", foreignKey: "business_id" },
    trusted_list: { type: "has_many", foreignKey: "business_id" },
    wallet_txns: { type: "has_many", foreignKey: "business_id" },
    attendance: { type: "has_many", foreignKey: "business_id" },
    pos_orders: { type: "has_many", foreignKey: "business_id" },
    sale_order: { type: "has_many", foreignKey: "business_id" },
    rate_list: { type: "has_many", foreignKey: "business_id" },
    customer: { type: "has_many", foreignKey: "business_id" },
    journal: { type: "has_many", foreignKey: "business_id" },
    invoice: { type: "has_many", foreignKey: "business_id" },
    payment: { type: "has_many", foreignKey: "business_id" },
    company: { type: "has_many", foreignKey: "business_id" },
    orders: { type: "has_many", foreignKey: "business_id" },
    groups: { type: "has_many", foreignKey: "business_id" },
    stock: { type: "has_many", foreignKey: "business_id" },
    staff: { type: "has_many", foreignKey: "business_id" },
    cart: { type: "has_many", foreignKey: "business_id" },
    unit: { type: "has_many", foreignKey: "business_id" },
    txns: { type: "has_many", foreignKey: "business_id" },
    cash_txn: { type: "has_many", foreignKey: "business_id" },
  };

  @field("online_payments_activated") online_payments_activated;
  @text("invoice_number_prefix") invoice_number_prefix;
  @field("invoice_number_count") invoice_number_count;
  @field("expense_number_count") expense_number_count;
  @field("quick_sale_number_count") quick_sale_number_count;
  @text("alternate_number") alternate_number;
  @text("beneficiary_name") beneficiary_name;
  @field("pos_order_count") pos_order_count;
  @text("account_number") account_number;
  @field("card_template") card_template;
  @text("new_category") new_category;
  @text("report_url") report_url;
  @field("group_type") group_type;
  @field("is_active") is_active;
  @field("is_synced") is_synced;
  @field("connected") connected;
  @field("longitude") longitude;
  @text("bank_name") bank_name;
  @text("image_url") image_url;
  @text("province") province;
  @field("latitude") latitude;
  @field('cashbook_migrated') cashbook_migrated;
  @text("landmark") landmark;
  @text("category") category;
  @field("personal") personal;
  @text("printer") printer;
  @text("address") address;
  @text("contact") contact;
  @text("name") name;
  @text("type") type;
  @text("sntn") sntn;
  @text("iban") iban;
  @text("city") city;
  @text("area") area;
  @text("ntn") ntn;

  @date("last_contact_sync_at") last_contact_sync_at;
  @date("last_active_at") last_active_at;
  @date("d1_at") d1_at;
  @date("d2_at") d2_at;

  @json("sms_template", sanitizeSmsTemplate) sms_template;
  @json("favourites", sanitizeFavourites) favourites;

  @relation("user", "user_id") user;

  @children("group_payment_ledger") group_payment_ledger;
  @children("cashbook_categories") cashbook_categories;
  @children("recharge_disputes") recharge_disputes;
  @children("stock_categories") stock_categories;
  @children("invoice_payments") invoice_payments;
  @children("survey_responses") survey_responses;
  @children("pos_order_lines") pos_order_lines;
  @children("rate_list_lines") rate_list_lines;
  @children("settings_config") settings_config;
  @children("inapp_messages") inapp_messages;
  @children("business_card") business_card;
  @children("trusted_list") trusted_list;
  @children("wallet_txns") wallet_txns;
  @children("attendance") attendance;
  @children("pos_orders") pos_orders;
  @children("sale_order") sale_order;
  @children("rate_list") rate_list;
  @children("customer") customer;
  @children("company") company;
  @children("journal") journal;
  @children("invoice") invoice;
  @children("payment") payment;
  @children("orders") orders;
  @children("groups") groups;
  @children("stock") stock;
  @children("staff") staff;
  @children("cart") cart;
  @children("unit") unit;
  @children("txns") txns;
  @children("cash_txn") cash_txn;

  @lazy getAllUnarchivedStockItems = () =>
    this.stock
      .extend(Q.where("archived", Q.notEq(true)))
      .observeWithColumns(["stock", "archived"]);
  @lazy getAllUnarchivedStockItemsCount = () =>
    this.stock.extend(Q.where("archived", Q.notEq(true))).observeCount();
  @lazy udhaarOverdueCustomers = this.customer.extend(
    Q.and(
      Q.where("total_outstanding_payment", Q.gt(0)),
      Q.where("collect_at", Q.notEq(null)),
    ),
  );
  @lazy getCustomersByNumber = this.customer.extend(
    Q.where("is_supplier", false),
    Q.where("phone", Q.notEq("")),
  );
  @lazy getSuppliersByNumber = this.customer.extend(
    Q.where("is_supplier", true),
    Q.where("phone", Q.notEq("")),
  );
  @lazy fetchStockById = (stockId) =>
    this.collections.get("stock").query(Q.where("id", stockId)).fetch();
  @lazy rechargeTransactions = this.wallet_txns.extend(
    Q.where("type", "recharge"),
  );
  @lazy CategoryProductList = this.stock_categories.extend(
    Q.or(Q.where("level", 1)),
  );
  @lazy getCustomers = this.customer.extend(Q.where("is_supplier", false));
  @lazy getSuppliers = this.customer.extend(Q.where("is_supplier", true));
  @lazy getAllCustomers = this.customer.extend();

  @lazy getMostSalesItemByDateAndSearchText = (
    start,
    end,
    searchTerm,
    limit,
    offset,
  ) => {
    let query = this.orders.extend(
      Q.and(
        Q.where("type", "sales"),
        Q.where("transacted_at", Q.lte(end)),
        Q.where("transacted_at", Q.gte(start)),
        Q.where("name", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
      ),
    );

    query = query.extend(Q.sortBy("transacted_at", Q.desc));

    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }
    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getUnarchivedStockByDateAndSearchText = (
    start,
    end,
    filters = {},
    limit,
    offset,
  ) => {
    const { items, sort, amount = {}, keywords = "" } = filters;

    const safeSearch = Q.sanitizeLikeString(keywords);

    const conditions = [
      Q.where("archived", Q.notEq(true)),
      Q.where("created_at", Q.between(start, end)),
    ];

    // Search Filter
    if (safeSearch) {
      conditions.push(
        Q.or(
          Q.where("name", Q.like(`%${safeSearch}%`)),
          Q.where("selling_price", Q.like(`%${safeSearch}%`)),
        ),
      );
    }

    // Low Stock Filter
    if (items === "Low stock items") {
      conditions.push(
        Q.or(
          Q.where("stock", Q.eq(null)),
          Q.and(
            Q.where("low_stock", Q.notEq(null)),
            Q.where("stock", Q.lte(Q.column("low_stock"))),
          ),
          Q.and(Q.where("low_stock", Q.eq(null)), Q.where("stock", Q.lte(5))),
        ),
      );
    }

    // Amount Filter
    const amountConditions = [];

    if (amount.min) {
      amountConditions.push(Q.where("selling_price", Q.gte(amount.min)));
    }

    if (amount.max) {
      amountConditions.push(Q.where("selling_price", Q.lte(amount.max)));
    }

    if (amountConditions.length) {
      conditions.push(Q.and(...amountConditions));
    }

    // Quantity sort: use unsafeLokiTransform so null stock sorts as 0.
    // Pagination is handled inside the transform because LokiJS applies
    // take/skip before the transform runs, which would break cross-page ordering.
    if (
      sort === NEW_SORT.quantityHighToLow ||
      sort === NEW_SORT.quantityLowToHigh
    ) {
      const desc = sort === NEW_SORT.quantityHighToLow;
      const _limit = limit;
      const _offset = offset ?? 0;

      return this.stock.extend(Q.and(...conditions)).extend(
        Q.unsafeLokiTransform((records) => {
          const sorted = [...records].sort((a, b) => {
            const sa = a.stock ?? 0;
            const sb = b.stock ?? 0;
            return desc ? sb - sa : sa - sb;
          });
          if (_limit !== undefined) {
            return sorted.slice(_offset, _offset + _limit);
          }
          return sorted;
        }),
      );
    }

    // ---------------------------------------------------
    // NORMAL WATERMELON QUERY
    // ---------------------------------------------------

    let query = this.stock.extend(Q.and(...conditions));

    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("selling_price", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("selling_price", Q.asc));
        break;

      case NEW_SORT.nameAToZ:
        query = query.extend(Q.sortBy("name", Q.asc));
        break;

      case NEW_SORT.nameZToA:
        query = query.extend(Q.sortBy("name", Q.desc));
        break;

      default:
        query = query.extend(Q.sortBy("created_at", Q.desc));
        break;
    }

    // Pagination
    if (limit !== undefined) query = query.extend(Q.take(limit));

    if (offset !== undefined) query = query.extend(Q.skip(offset));

    return query;
  };

  @lazy getTransactionByFilter = (
    start,
    end,
    searchTerm,
    paymentMethod = "",
  ) => {
    paymentMethod =
      paymentMethod === "All"
        ? ""
        : paymentMethod === "Online payment"
          ? "online"
          : paymentMethod;
    return this.txns.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      Q.and(
        Q.where("created_at", Q.gte(start)),
        Q.where("created_at", Q.lte(end)),
        Q.where(
          "payment_method",
          Q.like(`%${Q.sanitizeLikeString(paymentMethod)}%`),
        ),
        Q.or(
          Q.on(
            CUSTOMER_SCHEMA,
            Q.where("name", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
          ),
          Q.where("trnsId", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
          Q.where("amount", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
        ),
      ),
    );
  };
  @lazy getPosTransactionByFilter = (
    start,
    end,
    searchTerm,
    status = "all",
    paymentMethod = "all",
    limit,
    offset,
    sort,
  ) => {
    let filterStatusCondition;
    let filterPaymentCondition;
    switch (status) {
      case "all":
        filterStatusCondition = Q.where("status", Q.notEq(null));
        break;
      default:
        filterStatusCondition = Q.where("status", status);
    }
    switch (paymentMethod) {
      case "all":
        filterPaymentCondition = Q.where("payment_method", Q.notEq(null));
        break;
      default:
        filterPaymentCondition = Q.where("payment_method", paymentMethod);
    }
    let query = this.txns.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      filterStatusCondition,
      filterPaymentCondition,

      Q.or(
        Q.on(
          CUSTOMER_SCHEMA,
          Q.where("name", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
        ),
        Q.where("trnsId", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
        Q.where("amount", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
      ),
    );
    if (start && end)
      query = query.extend(
        Q.where("created_at", Q.gte(start)),
        Q.where("created_at", Q.lte(end)),
      );
    // Proper Stable Sorting
    switch (sort) {
      case SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case SORT.Oldest:
        query = query.extend(Q.sortBy("created_at", Q.asc));
        break;

      case SORT.Newest:
      default:
        query = query.extend(Q.sortBy("created_at", Q.desc));
        break;
    }

    // Pagination LAST (very important)
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getCategories = this.stock_categories.extend(Q.or(Q.where("level", 1)));
  @lazy getMostSalesItemByDate = (start, end) =>
    this.orders.extend(
      Q.and(
        Q.where("type", "sales"),
        Q.where("transacted_at", Q.lte(end)),
        Q.where("transacted_at", Q.gte(start)),
      ),
      // Q.experimentalSortBy('quantity', Q.desc)
    );

  @lazy getAllSalesByDate = (start, end) =>
    this.invoice.extend(
      Q.and(
        Q.where("type", Q.eq("in")),
        Q.where("transacted_at", Q.lte(end)),
        Q.where("transacted_at", Q.gte(start)),
      ),
    );

  @lazy getAllPurchaseByDate = (start, end) =>
    this.invoice.extend(
      Q.and(
        Q.where("type", Q.notEq("in")),
        Q.where("transacted_at", Q.lte(end)),
        Q.where("transacted_at", Q.gte(start)),
      ),
    );

  @lazy getPosSalesByDate = (start, end) =>
    this.txns.extend(
      Q.where("created_at", Q.lte(end)),
      Q.where("created_at", Q.gte(start)),
    );

  @lazy getSalesDatabyDate = (start, end) =>
    this.txns.extend(
      Q.and(
        Q.where("type", "in"),
        Q.where("created_at", Q.lte(end)),
        Q.where("created_at", Q.gte(start)),
      ),
      // Q.experimentalSortBy('created_at', Q.desc)
    );
  @lazy getBillInvoicesByDate = (start, end) =>
    this.invoice.extend(
      Q.and(
        Q.where("tranType", "bill"),
        Q.where("type", Q.notEq("in")),
        Q.where("transacted_at", Q.lte(end)),
        Q.where("transacted_at", Q.gte(start)),
      ),
      // Q.experimentalSortBy('transacted_at', Q.desc)
    );
  @lazy getMostPurchaseItemByDate = (start, end) =>
    this.orders.extend(
      Q.and(
        Q.where("type", "expense"),
        Q.where("transacted_at", Q.lte(end)),
        Q.where("transacted_at", Q.gte(start)),
      ),
      //  Q.experimentalSortBy('quantity', Q.desc)
    );

  @lazy getAllCustomerDataByDate = (start, end) =>
    this.customer.extend(
      (Q.where("last_transaction_at", Q.lte(end)),
        Q.where("last_transaction_at", Q.gte(start))),
      // Q.experimentalSortBy('last_transaction_at', Q.desc)
    );
  @lazy getAllPayments = (start, end) =>
    this.payment.extend(
      Q.where("transacted_at", Q.lte(end)),
      Q.where("transacted_at", Q.gte(start)),
      // Q.experimentalSortBy('transacted_at', Q.desc)
    );
  @lazy getUnarchivedStockDataByDate = (start, end) =>
    this.stock.extend(
      Q.where("archived", Q.notEq(true)),
      Q.where("created_at", Q.lte(end)),
      Q.where("created_at", Q.gte(start)),
      //  Q.experimentalSortBy('created_at', Q.desc)
    );

  @lazy getInvoiceReport = (
    start,
    end,
    searchTerm,
    type,
    status,
    sort,
    limit,
    offset,
    range,
    method,
  ) => {
    const conditions = [];

    conditions.push(Q.where("tranType", "bill"));

    if (type) {
      conditions.push(Q.where("type", type === "in" ? "in" : Q.notEq("in")));
    }

    if (start) conditions.push(Q.where("transacted_at", Q.gte(start)));
    if (end) conditions.push(Q.where("transacted_at", Q.lte(end)));

    if (status?.length) {
      let normalizedStatus = status.map((s) => s.toLowerCase());
      const hasOverdue = normalizedStatus?.includes("overdue");
      const hasUnpaid = normalizedStatus?.includes("unpaid");
      const hasPaid = normalizedStatus?.includes("paid");

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const now = today.getTime();

      const statusConditions = [];

      // Paid
      if (hasPaid) {
        statusConditions.push(Q.where("status", "paid"));
      }

      // Overdue (unpaid + due_date < now)
      if (hasOverdue) {
        statusConditions.push(
          Q.and(
            Q.where("status", "unpaid"),
            Q.where("due_date", Q.notEq(null)),
            Q.where("due_date", Q.notEq(0)),
            Q.where("due_date", Q.lt(now)),
          ),
        );
      }

      // Unpaid
      if (hasUnpaid) {
        statusConditions.push(Q.where("status", "unpaid"));
      }

      conditions.push(Q.or(...statusConditions));
    }

    if (method?.length) {
      const variations = method.flatMap((m) => [
        m.toLowerCase(),
        m.toUpperCase(),
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase(),
      ]);

      conditions.push(Q.where("method", Q.oneOf([...new Set(variations)])));
    }

    if (range?.min) conditions.push(Q.where("amount", Q.gte(range.min)));
    if (range?.max) conditions.push(Q.where("amount", Q.lte(range.max)));

    if (searchTerm?.trim()) {
      const sanitized = Q.sanitizeLikeString(searchTerm);
      conditions.push(
        Q.or(
          Q.on(CUSTOMER_SCHEMA, Q.where("name", Q.like(`%${sanitized}%`))),
          Q.where("invoice_prefix", Q.like(`%${sanitized}%`)),
          Q.where("invoice_number", Q.like(`%${sanitized}%`)),
        ),
      );
    }

    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      ...conditions,
    );

    // Sorting
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.blank:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      case NEW_SORT.paid:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination (last!)
    if (limit !== undefined) query = query.extend(Q.take(limit));
    if (offset !== undefined) query = query.extend(Q.skip(offset));

    return query;
  };

  @lazy getExpense = (
    start,
    end,
    searchTerm,
    status,
    limit,
    offset,
    sort,
    range,
    categories,
    method,
  ) => {
    const filterEndDateCondition = end
      ? Q.where("transacted_at", Q.lte(end))
      : Q.where("transacted_at", Q.notEq(null));

    const filterStartDateCondition = start
      ? Q.where("transacted_at", Q.gte(start))
      : Q.where("transacted_at", Q.notEq(null));

    let filterRangeCondition = [];

    if (range?.min) {
      filterRangeCondition.push(Q.where("amount", Q.gte(range.min)));
    }

    if (range?.max) {
      filterRangeCondition.push(Q.where("amount", Q.lte(range.max)));
    }

    let filterStatusCondition = [];

    if (status?.length > 0) {
      const normalizedStatus = status.map((s) => s.toLowerCase());

      let conditions = [];

      // PAID
      if (normalizedStatus.includes("paid")) {
        conditions.push(
          Q.or(
            Q.where("status", "paid"),
            Q.and(
              Q.where("status", null), // ONLY null, not notEq
              Q.where("amount", Q.eq(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      // UNPAID
      if (normalizedStatus.includes("unpaid")) {
        conditions.push(
          Q.or(
            Q.where("status", "unpaid"),
            Q.and(
              Q.where("status", null), // ONLY null
              Q.where("amount", Q.gt(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      if (conditions.length > 0) {
        filterStatusCondition.push(Q.or(...conditions));
      }
    }

    let filterCategoryCondition = [];
    if (categories?.length > 0) {
      filterCategoryCondition.push(Q.where("category", Q.oneOf(expandCategoryFilterValues(categories, ["business_expense"]))));
    }

    let paymentMethodCondition = [];

    if (method?.length > 0) {
      const variations = method.flatMap((m) => [
        m.toLowerCase(),
        m.toUpperCase(),
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase(),
      ]);

      paymentMethodCondition.push(
        Q.where("method", Q.oneOf([...new Set(variations)])),
      );
    }

    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      Q.where("tranType", Q.notEq("bill")),
      filterEndDateCondition,
      filterStartDateCondition,
      Q.where("type", "out"),
      ...filterStatusCondition,
      ...filterRangeCondition,
      ...filterCategoryCondition,
      ...paymentMethodCondition,
      Q.or(
        Q.on(
          CUSTOMER_SCHEMA,
          Q.where(
            "name",
            Q.like(`%${Q.sanitizeLikeString(searchTerm || "")}%`),
          ),
        ),
        Q.where(
          "invoice_prefix",
          Q.like(`%${Q.sanitizeLikeString(searchTerm || "")}%`),
        ),
        Q.where(
          "invoice_number",
          Q.like(`%${Q.sanitizeLikeString(searchTerm || "")}%`),
        ),
      ),
    );

    // Proper Stable Sorting
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.blank:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination LAST (very important)
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getQuickSale = (
    start,
    end,
    searchTerm,
    status,
    limit,
    offset,
    sort,
    range,
    categories,
    method,
  ) => {
    let filterEndDateCondition = end
      ? Q.where("transacted_at", Q.lte(end))
      : Q.where("transacted_at", Q.notEq(null));
    let filterStartDateCondition = start
      ? Q.where("transacted_at", Q.gte(start))
      : Q.where("transacted_at", Q.notEq(null));

    let filterRangeCondition = [];

    if (range?.min) {
      filterRangeCondition.push(Q.where("amount", Q.gte(range.min)));
    }

    if (range?.max) {
      filterRangeCondition.push(Q.where("amount", Q.lte(range.max)));
    }

    let filterStatusCondition = [];

    if (status?.length > 0) {
      const normalizedStatus = status.map((s) => s.toLowerCase());

      let conditions = [];

      // PAID
      if (normalizedStatus.includes("paid")) {
        conditions.push(
          Q.or(
            Q.where("status", "paid"),
            Q.and(
              Q.where("status", null), // ONLY null, not notEq
              Q.where("amount", Q.eq(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      // UNPAID
      if (normalizedStatus.includes("unpaid")) {
        conditions.push(
          Q.or(
            Q.where("status", "unpaid"),
            Q.and(
              Q.where("status", null), // ONLY null
              Q.where("amount", Q.gt(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      if (conditions.length > 0) {
        filterStatusCondition.push(Q.or(...conditions));
      }
    }

    let filterCategoryCondition = [];
    if (categories?.length > 0) {
      filterCategoryCondition.push(Q.where("category", Q.oneOf(expandCategoryFilterValues(categories, ["business_sale"]))));
    }

    let paymentMethodCondition = [];

    if (method?.length > 0) {
      const variations = method.flatMap((m) => [
        m.toLowerCase(),
        m.toUpperCase(),
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase(),
      ]);

      paymentMethodCondition.push(
        Q.where("method", Q.oneOf([...new Set(variations)])),
      );
    }

    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      Q.where("tranType", Q.notIn(["bill", "pos"])),
      filterEndDateCondition,
      filterStartDateCondition,
      Q.where("type", "in"),
      ...filterStatusCondition,
      ...filterRangeCondition,
      ...filterCategoryCondition,
      ...paymentMethodCondition,
      Q.or(
        Q.on(
          CUSTOMER_SCHEMA,
          Q.where("name", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
        ),
        Q.where(
          "invoice_prefix",
          Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`),
        ),
        Q.where(
          "invoice_number",
          Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`),
        ),
      ),
    );
    // Proper Stable Sorting
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.blank:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination LAST (very important)
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getCashTxn = (start, end, searchTerm = "", view, limit, offset, sort, range, categories) => {
    let filterEndDateCondition = end
      ? Q.where("transacted_at", Q.lte(end))
      : Q.where("transacted_at", Q.notEq(null));
    let filterStartDateCondition = start
      ? Q.where("transacted_at", Q.gte(start))
      : Q.where("transacted_at", Q.notEq(null));

    let filterRangeCondition = [];
    if (range?.min) filterRangeCondition.push(Q.where("amount", Q.gte(range.min)));
    if (range?.max) filterRangeCondition.push(Q.where("amount", Q.lte(range.max)));

    let filterViewCondition = [];
    if (view) {
      filterViewCondition.push(Q.where("type", view));
    }

    let filterCategoryCondition = [];
    if (categories?.length > 0)
      filterCategoryCondition.push(
        Q.where(
          "category",
          Q.oneOf(
            expandCategoryFilterValues(categories, ["cb_cash_in", "cb_cash_out"]),
          ),
        ),
      );

    let query = this.cash_txn.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      filterEndDateCondition,
      filterStartDateCondition,
      ...filterViewCondition,
      ...filterRangeCondition,
      ...filterCategoryCondition,
      Q.or(
        Q.on(CUSTOMER_SCHEMA, Q.where("name", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`))),
        Q.where("note", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
        Q.where("invoice_number", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
      ),
    );

    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;
      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;
      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;
      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    if (limit !== undefined) query = query.extend(Q.take(limit));
    if (offset !== undefined) query = query.extend(Q.skip(offset));

    return query;
  };

  @lazy getAllSales = (
    start,
    end,
    searchTerm,
    status,
    limit,
    offset,
    sort,
    range,
    view,
    method,
  ) => {
    let filterEndDateCondition = end
      ? Q.where("transacted_at", Q.lte(end))
      : Q.where("transacted_at", Q.notEq(null));
    let filterStartDateCondition = start
      ? Q.where("transacted_at", Q.gte(start))
      : Q.where("transacted_at", Q.notEq(null));

    let filterRangeCondition = [];

    if (range?.min) {
      filterRangeCondition.push(Q.where("amount", Q.gte(range.min)));
    }

    if (range?.max) {
      filterRangeCondition.push(Q.where("amount", Q.lte(range.max)));
    }

    let filterStatusCondition = [];

    if (status?.length > 0) {
      const normalizedStatus = status.map((s) => s.toLowerCase());

      let conditions = [];

      // PAID
      if (normalizedStatus.includes("paid")) {
        conditions.push(
          Q.or(
            Q.where("status", "paid"),
            Q.and(
              Q.where("status", null), // ONLY null, not notEq
              Q.where("amount", Q.eq(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      // UNPAID
      if (normalizedStatus.includes("unpaid")) {
        conditions.push(
          Q.or(
            Q.where("status", "unpaid"),
            Q.and(
              Q.where("status", null), // ONLY null
              Q.where("amount", Q.gt(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      if (conditions.length > 0) {
        filterStatusCondition.push(Q.or(...conditions));
      }
    }

    let filterTypeCondition = [];

    if (view) {
      const normalizedView = view.toLowerCase();

      if (normalizedView === "invoice") {
        filterTypeCondition.push(Q.where("tranType", "bill"));
      } else if (normalizedView === "pos") {
        filterTypeCondition.push(Q.where("tranType", "pos"));
      } else {
        // Exclude both bill and pos
        filterTypeCondition.push(Q.where("tranType", Q.notIn(["bill", "pos"])));
      }
    }

    let paymentMethodCondition = [];

    if (method?.length > 0) {
      const variations = method.flatMap((m) => [
        m.toLowerCase(),
        m.toUpperCase(),
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase(),
      ]);

      paymentMethodCondition.push(
        Q.where("method", Q.oneOf([...new Set(variations)])),
      );
    }

    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      filterEndDateCondition,
      filterStartDateCondition,
      Q.where("type", "in"),
      ...filterStatusCondition,
      ...filterRangeCondition,
      ...filterTypeCondition,
      ...paymentMethodCondition,
      Q.or(
        Q.on(
          CUSTOMER_SCHEMA,
          Q.where("name", Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`)),
        ),
        Q.where(
          "invoice_prefix",
          Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`),
        ),
        Q.where(
          "invoice_number",
          Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`),
        ),
      ),
    );

    // Proper Stable Sorting
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.invoice:
        query = query.extend(Q.sortBy("tranType", Q.asc));
        break;

      case NEW_SORT.pos:
        query = query.extend(Q.sortBy("tranType", Q.desc));
        break;

      case NEW_SORT.blank:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination LAST (very important)
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getAllRecentTransactions = (
    start,
    end,
    searchTerm,
    limit,
    offset,
    sort,
    range,
    type,
    status,
    method,
  ) => {
    let filterEndDateCondition = end
      ? Q.where("transacted_at", Q.lte(end))
      : Q.where("transacted_at", Q.notEq(null));
    let filterStartDateCondition = start
      ? Q.where("transacted_at", Q.gte(start))
      : Q.where("transacted_at", Q.notEq(null));

    let filterRangeCondition = [];

    if (range?.min) {
      filterRangeCondition.push(Q.where("amount", Q.gte(range.min)));
    }

    if (range?.max) {
      filterRangeCondition.push(Q.where("amount", Q.lte(range.max)));
    }

    let filterStatusCondition = [];

    if (status?.length > 0) {
      const normalizedStatus = status.map((s) => s.toLowerCase());

      let conditions = [];

      // PAID
      if (normalizedStatus.includes("paid")) {
        conditions.push(
          Q.or(
            Q.where("status", "paid"),
            Q.and(
              Q.where("status", null), // ONLY null, not notEq
              Q.where("amount", Q.eq(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      // UNPAID
      if (normalizedStatus.includes("unpaid")) {
        conditions.push(
          Q.or(
            Q.where("status", "unpaid"),
            Q.and(
              Q.where("status", null), // ONLY null
              Q.where("amount", Q.gt(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      if (conditions.length > 0) {
        filterStatusCondition.push(Q.or(...conditions));
      }
    }

    let paymentMethodCondition = [];

    if (method?.length > 0) {
      const variations = method.flatMap((m) => [
        m.toLowerCase(),
        m.toUpperCase(),
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase(),
      ]);

      paymentMethodCondition.push(
        Q.where("method", Q.oneOf([...new Set(variations)])),
      );
    }

    let filterTypeCondition = [];

    if (type?.length) {
      const mappedTypes = [];

      if (type.includes("sale")) {
        mappedTypes.push("in");
      }

      if (type.includes("expense")) {
        mappedTypes.push("out");
      }

      if (mappedTypes.length) {
        filterTypeCondition.push(Q.where("type", Q.oneOf(mappedTypes)));
      }
    }

    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      filterEndDateCondition,
      filterStartDateCondition,
      ...filterRangeCondition,
      ...filterTypeCondition,
      ...filterStatusCondition,
      ...paymentMethodCondition,
      Q.or(
        Q.where(
          "invoice_prefix",
          Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`),
        ),
        Q.where(
          "invoice_number",
          Q.like(`%${Q.sanitizeLikeString(searchTerm)}%`),
        ),
      ),
    );

    // Proper Stable Sorting
    switch (sort) {
      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.SALE:
        query = query.extend(Q.sortBy("type", Q.asc));
        break;

      case NEW_SORT.EXPENSE:
        query = query.extend(Q.sortBy("type", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.blank:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination LAST (very important)
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getPOSReport = (start, end, filters = {}, limit, offset) => {
    let { status, method, sort, amount, keywords } = filters;

    // Date Filters
    let filterEndDateCondition = end
      ? Q.where("transacted_at", Q.lte(end))
      : Q.where("transacted_at", Q.notEq(null));

    let filterStartDateCondition = start
      ? Q.where("transacted_at", Q.gte(start))
      : Q.where("transacted_at", Q.notEq(null));

    // Range Filter
    let filterRangeCondition = [];

    if (amount?.min) {
      filterRangeCondition.push(Q.where("amount", Q.gte(amount.min)));
    }

    if (amount?.max) {
      filterRangeCondition.push(Q.where("amount", Q.lte(amount.max)));
    }

    // Status Filter (same as Quick Sale)
    let filterStatusCondition = [];

    if (status?.length > 0) {
      const normalizedStatus = status.map((s) => s.toLowerCase());

      let conditions = [];

      // PAID
      if (normalizedStatus.includes("paid")) {
        conditions.push(
          Q.or(
            Q.where("status", "paid"),
            Q.and(
              Q.where("status", null),
              Q.where("amount", Q.eq(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      // UNPAID
      if (normalizedStatus.includes("unpaid")) {
        conditions.push(
          Q.or(
            Q.where("status", "unpaid"),
            Q.and(
              Q.where("status", null),
              Q.where("amount", Q.gt(Q.column("advance_amount"))),
            ),
          ),
        );
      }

      if (conditions.length > 0) {
        filterStatusCondition.push(Q.or(...conditions));
      }
    }

    let paymentMethodCondition = [];

    if (method?.length > 0) {
      const normalizedMethods = method.map((m) => m.toLowerCase());

      let conditions = [];

      // BANK
      if (normalizedMethods.includes("bank")) {
        conditions.push(
          Q.or(Q.where("method", "bank"), Q.where("method", "Bank")),
        );
      }

      // CASH (cash OR null)
      if (normalizedMethods.includes("cash")) {
        conditions.push(
          Q.or(
            Q.where("method", "cash"),
            Q.where("method", "Cash"),
            Q.where("method", null),
          ),
        );
      }

      if (conditions.length > 0) {
        paymentMethodCondition.push(Q.or(...conditions));
      }
    }

    let searchCondition = [];

    if (keywords) {
      searchCondition.push(
        Q.or(
          Q.on(
            CUSTOMER_SCHEMA,
            Q.where("name", Q.like(`%${Q.sanitizeLikeString(keywords)}%`)),
          ),
          Q.where(
            "invoice_prefix",
            Q.like(`%${Q.sanitizeLikeString(keywords)}%`),
          ),
          Q.where(
            "invoice_number",
            Q.like(`%${Q.sanitizeLikeString(keywords)}%`),
          ),
        ),
      );
    }

    // Query
    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      Q.where("tranType", "pos"),
      filterEndDateCondition,
      filterStartDateCondition,
      Q.where("type", "in"),
      ...filterStatusCondition,
      ...filterRangeCondition,
      ...paymentMethodCondition,
      ...searchCondition,
    );

    // Sorting
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.paid:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination
    if (limit) {
      query = query.extend(Q.take(limit));
    }

    if (offset) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy getUncategorizedInvoice = (
    start,
    end,
    searchTerm,
    type,
    limit,
    offset,
    range,
    category,
    sort,
  ) => {
    const conditions = [];
    if (category) {
      // The stored `category` column may hold either the legacy translated
      // name (older records) or the category id (newer records) — match
      // both representations so a name-keyed bucket still finds id-based rows.
      const categoryGroup =
        type?.toLowerCase() === "sale" ? "business_sale" : "business_expense";
      const matchingIds = findCategoryIdsByName(categoryGroup, category);
      // The bucket label is in the current language, but legacy rows may have
      // stored the name in another language — match those translations exactly
      // (only the label itself keeps the original partial match)
      const otherNames = getCategoryNamesByIds(categoryGroup, matchingIds).filter(
        (name) => name !== category,
      );
      const exactValues = [...matchingIds, ...otherNames];
      conditions.push(
        exactValues.length
          ? Q.or(
            Q.where("category", Q.like(`%${category}%`)),
            Q.where("category", Q.oneOf(exactValues)),
          )
          : Q.where("category", Q.like(`%${category}%`)),
      );
    } else
      conditions.push(Q.or(Q.where("category", null), Q.where("category", "")));

    if (type) {
      conditions.push(
        Q.where("type", type?.toLowerCase() === "sale" ? "in" : Q.notEq("in")),
      );
    }

    if (start) conditions.push(Q.where("transacted_at", Q.gte(start)));
    if (end) conditions.push(Q.where("transacted_at", Q.lte(end)));

    if (range?.min) conditions.push(Q.where("amount", Q.gte(range.min)));
    if (range?.max) conditions.push(Q.where("amount", Q.lte(range.max)));

    if (searchTerm?.trim()) {
      const sanitized = Q.sanitizeLikeString(searchTerm);
      conditions.push(
        Q.or(
          Q.where("invoice_prefix", Q.like(`%${sanitized}%`)),
          Q.where("invoice_number", Q.like(`%${sanitized}%`)),
        ),
      );
    }
    let query = this.invoice.extend(
      Q.experimentalJoinTables([CUSTOMER_SCHEMA]),
      ...conditions,
    );

    switch (sort) {
      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;

      case NEW_SORT.SALE:
        query = query.extend(Q.sortBy("type", Q.asc));
        break;

      case NEW_SORT.EXPENSE:
        query = query.extend(Q.sortBy("type", Q.desc));
        break;

      case NEW_SORT.unpaid:
        query = query.extend(Q.sortBy("status", Q.desc));
        break;

      case NEW_SORT.blank:
        query = query.extend(Q.sortBy("status", Q.asc));
        break;

      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }

    // Pagination (last!)
    if (limit !== undefined) query = query.extend(Q.take(limit));
    if (offset !== undefined) query = query.extend(Q.skip(offset));

    return query;
  };

  @lazy getProfitAndLossStatement = async (
    start,
    end,
    { skipCategories = false } = {},
  ) => {
    const dateConditions = [];

    if (start) dateConditions.push(Q.where("transacted_at", Q.gte(start)));
    if (end) dateConditions.push(Q.where("transacted_at", Q.lte(end)));

    const [salesInvoices, expenseInvoices] =
      await Promise.all([
        this.invoice.extend(Q.where("type", "in"), ...dateConditions).fetch(),
        this.invoice.extend(Q.where("type", "out"), ...dateConditions).fetch(),
      ]);

    const salesIds = salesInvoices.map((i) => i.id);
    const expenseIds = expenseInvoices.map((i) => i.id);

    const [salesOrders, expenseOrders] = await Promise.all([
      salesIds.length
        ? this.orders.extend(Q.where("invoice_id", Q.oneOf(salesIds))).fetch()
        : [],
      expenseIds.length
        ? this.orders.extend(Q.where("invoice_id", Q.oneOf(expenseIds))).fetch()
        : [],
    ]);

    const round2 = (n) => Math.round(n * 100) / 100;
    const catKey = (raw, group) => {
      const resolved = resolveCategoryDisplayName(group, raw);
      return (resolved?.trim() || "uncategorized").toLowerCase();
    };

    let totalSales = 0;
    let totalExpenses = 0;
    let costOfSales = 0;
    let expenseItemCost = 0;

    const salesCategoryRaw = new Map();
    const expensesCategoryRaw = new Map();

    // Sales invoices
    for (const inv of salesInvoices) {
      const amount = inv.amount || 0;

      totalSales += amount;

      if (!skipCategories) {
        const key = catKey(inv.category, "business_sale");
        salesCategoryRaw.set(key, (salesCategoryRaw.get(key) || 0) + amount);
      }
    }

    // Sales order costs (invoice-based)
    for (const ord of salesOrders) {
      costOfSales += (ord.cost_price || 0) * (ord.quantity || 1);
    }

    // Expense invoice totals
    for (const inv of expenseInvoices) {
      const orders = await inv.orders.fetch();

      if (orders.length === 0) {
        totalExpenses += inv.amount || 0;
      }
    }

    let netExpenses = totalExpenses;

    if (!skipCategories) {
      for (const inv of expenseInvoices) {
        const orders = await inv.orders.fetch();

        if (orders.length === 0) {
          const key = catKey(inv.category, "business_expense");

          expensesCategoryRaw.set(
            key,
            (expensesCategoryRaw.get(key) || 0) + (inv.amount || 0),
          );
        }
      }
    }

    const grossProfit = totalSales - costOfSales;
    const netProfit = grossProfit - netExpenses;

    const result = {
      totalSales: round2(totalSales),
      costOfSales: round2(costOfSales),
      grossProfit: round2(grossProfit),
      totalExpenses: round2(totalExpenses),
      expenseItemCost: round2(expenseItemCost),
      netExpenses: round2(netExpenses),
      netProfit: round2(netProfit),
    };

    if (skipCategories) {
      return result;
    }

    result.salesCategory = Object.fromEntries(
      [...salesCategoryRaw.entries()].map(([k, v]) => [k, round2(v)]),
    );

    result.expensesCategory = Object.fromEntries(
      [...expensesCategoryRaw.entries()]
        .map(([k, v]) => [k, round2(v)])
        .filter(([, v]) => v !== 0),
    );

    return result;
  };

  @lazy getCashFlowStatement = async (
    start,
    end,
    { skipCategories = false } = {},
  ) => {
    const conditions = [];

    if (start) conditions.push(Q.where("transacted_at", Q.gte(start)));
    if (end) conditions.push(Q.where("transacted_at", Q.lte(end)));

    const hasPayment = Q.or(
      Q.where("status", "paid"),
      Q.where("advance_amount", Q.gt(0)),
    );

    const [salesInvoices, expenseInvoices] = await Promise.all([
      this.invoice
        .extend(Q.where("type", "in"), hasPayment, ...conditions)
        .fetch(),
      this.invoice
        .extend(Q.where("type", "out"), hasPayment, ...conditions)
        .fetch(),
    ]);

    const round2 = (value) => Math.round(value * 100) / 100;

    const getCashAmount = (invoice) =>
      invoice.status === "paid"
        ? Number(invoice.amount || 0)
        : Number(invoice.advance_amount || 0);

    let moneyIn = 0;
    let moneyOut = 0;

    const salesCategory = skipCategories ? null : new Map();
    const expensesCategory = skipCategories ? null : new Map();

    const processInvoices = (invoices, categoryMap, isIncome) => {
      let total = 0;

      for (const invoice of invoices) {
        const amount = getCashAmount(invoice);

        total += amount;

        if (categoryMap) {
          const resolved = resolveCategoryDisplayName(
            isIncome ? "business_sale" : "business_expense",
            invoice.category,
          );
          const key = resolved?.trim() || "uncategorized";

          categoryMap.set(key, (categoryMap.get(key) || 0) + amount);
        }
      }

      return total;
    };

    moneyIn = processInvoices(salesInvoices, salesCategory, true);

    moneyOut = processInvoices(expenseInvoices, expensesCategory, false);

    const result = {
      moneyIn: round2(moneyIn),
      moneyOut: round2(moneyOut),
      netCashFlow: round2(moneyIn - moneyOut),
    };

    if (skipCategories) {
      return result;
    }

    const mapToObject = (map) =>
      Object.fromEntries(
        [...map.entries()]
          .map(([key, value]) => [key, round2(value)])
          .filter(([, value]) => value !== 0),
      );

    return {
      ...result,
      salesCategory: mapToObject(salesCategory),
      expensesCategory: mapToObject(expensesCategory),
    };
  };

  @writer async updateCoordinates(details) {
    await this.update((business) => {
      business.longitude = details.longitude;
      business.latitude = details.latitude;
    });
  }

  @writer async createCart() {
    return await this.collections.get(CART_SCHEMA).create((cart) => {
      cart.business.set(this);
      cart.created_at = new Date();
    });
  }

  @writer async updateName(updatedName) {
    await this.update((business) => {
      business.name = updatedName;
      business.is_active = true;
      business.last_active_at = new Date().getTime();
    });
  }

  @writer async updateBusiness(details) {
    return await this.update((business) => {
      business.name = details.name;
      business.category = details.category;
      details.landmark && (business.landmark = details.landmark);
    });
  }

  @writer async activateBusiness() {
    return await this.update((business) => {
      business.is_active = true;
      business.last_active_at = new Date().getTime();
    });
  }

  @writer async updateBusinessInvoiceNo(invoice_count) {
    return await this.update((business) => {
      business.invoice_number_count = invoice_count;
    });
  }

  @writer async updateBusinessExpenseNo(count) {
    return await this.update((business) => {
      business.expense_number_count = count;
    });
  }
  @writer async updateBusinessQuickSaleNo(count) {
    return await this.update((business) => {
      business.quick_sale_number_count = count;
    });
  }

  @writer async updateBusinessInvoicePrefix(invoice_prefix) {
    return await this.update((business) => {
      business.invoice_number_prefix = invoice_prefix || "INV#";
    });
  }

  @writer async deactivateBusiness() {
    await this.update((business) => {
      business.is_active = false;
    });
  }

  @writer async createCustomer(details) {
    return await this.collections.get(CUSTOMER_SCHEMA).create((customer) => {
      customer.business.set(this);
      customer._raw.id = details.id ? details.id : uuid();
      customer.name = details.name;
      customer.phone = details.phone;
      customer.total_outstanding_payment = details.total_outstanding_payment;
      customer.transaction_sms =
        details?.transaction_sms || details.phone ? true : false;
      customer.created_at = details.created_at
        ? details.created_at
        : new Date();
      customer.last_transaction_at = details.created_at
        ? details.created_at
        : new Date();
      customer.collect_at = details.collect_at ? details.collect_at : null;
      customer.is_supplier = details.is_supplier;
    });
  }

  @writer async createInvoice(details) {
    return await this.collections.get(INVOICE_SCHEMA).create((invoice) => {
      invoice.business.set(this);
      details.customer && invoice.customer.set(details.customer);
      details.company && invoice.company.set(details.company);
      invoice._raw.id = details.id ? details.id : uuid();
      invoice.sales_amount = details.sales_amount;
      invoice.cost_amount = details.cost_amount;
      invoice.amount = details.amount;
      invoice.total = details.total;
      invoice.sub_total = details.sub_total;
      invoice.discount = details.discount;
      invoice.discount_unit = details.discount_unit;
      invoice.tax_title = details.tax_title;
      invoice.tax_rate = details.tax_rate;
      invoice.advance_amount = details.advance_amount;
      invoice.invoice_title = details.invoice_title;
      invoice.invoice_number = details.invoice_number;
      invoice.invoice_prefix = details.invoice_prefix;
      invoice.signature = details.signature;
      invoice.profit = details.profit;
      invoice.attachment = details.attachment;
      invoice.transacted_at = details.date;
      invoice.due_date = details.due_date;
      invoice.image_url = details.image_url;
      invoice.document_url = details.document_url;
      invoice.note = details.note;
      invoice.status = details.status;
      invoice.tranType = details.tranType;
      invoice.method = details.method;
      invoice.type = details.type;
      invoice.bank = details.bank;
      invoice.wallet = details.wallet;
      invoice.category = details.category;
      invoice.payment_method = details.payment_method;
      invoice.change_amount = details.change_amount;
      invoice.recording = details?.recording_uri || null;
      invoice.created_at = new Date().getTime();
      invoice.reference_id = details.reference_id;
    });
  }

  @writer async createCashTxn(details) {
    return await this.collections.get(CASH_TXN_SCHEMA).create((cashTxn) => {
      cashTxn.business.set(this);
      details.customer && cashTxn.customer.set(details.customer);
      cashTxn._raw.id = details.id ? details.id : uuid();
      cashTxn.sales_amount = details.sales_amount;
      cashTxn.cost_amount = details.cost_amount;
      cashTxn.amount = details.amount;
      cashTxn.total = details.total;
      cashTxn.sub_total = details.sub_total;
      cashTxn.discount = details.discount;
      cashTxn.discount_unit = details.discount_unit;
      cashTxn.tax_title = details.tax_title;
      cashTxn.tax_rate = details.tax_rate;
      cashTxn.advance_amount = details.advance_amount;
      cashTxn.invoice_title = details.invoice_title;
      cashTxn.invoice_number = details.invoice_number;
      cashTxn.invoice_prefix = details.invoice_prefix;
      cashTxn.signature = details.signature;
      cashTxn.profit = details.profit;
      cashTxn.attachment = details.attachment;
      cashTxn.transacted_at = details.date;
      cashTxn.image_url = details.image_url;
      cashTxn.document_url = details.document_url;
      cashTxn.note = details.note;
      cashTxn.status = details.status;
      cashTxn.tranType = details.tranType;
      cashTxn.method = details.method;
      cashTxn.type = details.type;
      cashTxn.bank = details.bank;
      cashTxn.wallet = details.wallet;
      cashTxn.category = details.category;
      cashTxn.recording = details?.recording_uri || null;
      cashTxn.created_at = new Date().getTime();
    });
  }

  @writer async createPOSInvoice(details) {
    await this.batch(
      this.collections.get(POS_ORDERS_SCHEMA).prepareCreate((pos) => {
        pos.business.set(this);
        details.customer && pos.customer.set(details.customer);
        pos._raw.id = details.id ? details.id : uuid();
        pos.amount = details.amount;
        pos.amount_received = details.amount_received;
        pos.image_url = details.image_url;
        pos.note = details.note;
        pos.method = details.method;
        pos.tax = details.tax;
        pos.transacted_at = details.date;
        pos.count = details.count;
      }),
      this.prepareUpdate((business) => {
        business.pos_order_count += 1;
      }),
    );
  }

  @writer async createStock(details) {
    return await this.collections.get(STOCK_SCHEMA).create((stock) => {
      stock.business.set(this);
      details.category && stock.stock_categories.set(details.category);
      stock._raw.id = details?.id ? details.id : uuid();
      stock.name = details.name;
      stock.stock = details.stock;
      stock.cost_price = details.cost_price;
      stock.selling_price = details.selling_price;
      stock.created_at = new Date().getTime();
      stock.unit = details.unit;
      // details.bar_code && (stock.bar_code = details.bar_code);
      stock.tax = details.tax;
      stock.inclusive = details.inclusive || true;
      stock.secondary_unit = details.secondary_unit;
      stock.unit_conversion = details.unit_conversion;
      stock.imei = details.imei;
      stock.batch_number = details.batch_number;
      stock.barcode = details.barcode;
      stock.wholesale_price = details.wholesale_price;
      stock.expiry_date = details.expiry_date;
      stock.manufacturing_date = details.manufacturing_date;
      stock.minimum_order_quantity = details.minimum_order_quantity;
      stock.image_url = details.image_url;
      stock.low_stock = details.low_stock;
      stock.expiry_alert = details.expiry_alert;
      stock.sku = details.sku;
    });
  }

  @writer async createStaff(details) {
    await this.collections.get(STAFF_SCHEMA).create((staff) => {
      staff.business.set(this);
      staff._raw.id = details.id ? details.id : uuid();
      staff.name = details.name;
      staff.phone_number = details.phone_number;
      staff.salary = details.salary;
      staff.salary_type = details.salary_type;
      staff.working_hours = details.working_hours;
      staff.workdays = details.workdays;
      staff.open_balance = details.open_balance;
      staff.job_title = details.job_title;
      staff.joined_at = details.joined_at;
      staff.created_at = details.created_at
        ? details.created_at
        : new Date().getTime();
      staff.last_updated_at = details.last_updated_at
        ? details.last_updated_at
        : new Date().getTime();
      staff.auto_present = details.auto_present;
      staff.sms_alert = details.sms_alert;
    });
  }

  @writer async markAsDeleted() {
    await super.markAsDeleted();
  }

  @writer async setPrinter(value) {
    await this.update((business) => {
      business.printer = value;
      business.connected = true;
    });
  }

  @writer async togglePrinter(value) {
    await this.update((business) => {
      business.connected = value;
    });
  }

  @writer async createMasterCategory(details) {
    return await this.collections.get(CATEGORY_SCHEMA).create((category) => {
      category.business.set(this);
      category._raw.id = uuid();
      category.name = details.name;
      category.image_url = details.image_url;
      category.level = details.level;
    });
  }

  @writer async createCategory(details) {
    return await this.collections.get(CATEGORY_SCHEMA).create((category) => {
      category.business.set(this);
      category.reference.set(details.category);
      category._raw.id = uuid();
      category.name = details.name;
      category.image_url = details.image_url;
      category.level = details.level;
    });
  }

  @writer async createSaleOrder({ status }) {
    return await this.collections.get(SALE_ORDER_SCHEMA).create((saleOrder) => {
      saleOrder.business.set(this);
      saleOrder._raw.id = uuid();
      saleOrder.status = status;
      saleOrder.created_at = new Date();
    });
  }
  @writer async updateBusinessReportUrl(details) {
    await this.update((business) => {
      business.report_url = details.report_url;
    });
  }

  @writer async createPosTransaction(details) {
    return await this.collections.get(POS_TXNS_SCHEMA).create((pos_tsnx) => {
      pos_tsnx.business.set(this);
      details.customer && pos_tsnx.customer.set(details.customer);
      pos_tsnx._raw.id = details.id ? details.id : uuid();
      pos_tsnx.amount = Number(details.amount || 0);
      pos_tsnx.created_at = details.created_at;
      pos_tsnx.note = details.note;
      pos_tsnx.payment_method = details.payment_method;
      pos_tsnx.type = details.type;
      pos_tsnx.status = details.status;
      pos_tsnx.discount_unit = details.discount_unit || "rs";
      pos_tsnx.discount = Number(details.discount || 0);
      pos_tsnx.change_amount = Number(details.change_amount || 0);
      pos_tsnx.tax = Number(details.tax || 0);
      pos_tsnx.tax_type = details?.tax_type || "%";
      pos_tsnx.trnsId = details?.trnsId || "";
    });
  }

  @writer async createUnit(val) {
    return await this.collections.get(UNIT_SCHEMA).create((unit) => {
      unit.business.set(this);
      unit.name = val;
    });
  }

  @writer async createCashbookCategory(name) {
    return await this.collections
      .get(CASHBOOK_CATEGORIES)
      .create((category) => {
        category.business.set(this);
        category._raw.id = uuid();
        category.name = name;
      });
  }

  @writer async createCompany(name) {
    return await this.collections.get(COMPANY).create((company) => {
      company.business.set(this);
      company._raw.id = uuid();
      company.name = name;
    });
  }
}
